import { Component, inject, OnInit } from "@angular/core";
import { MenuItem } from "primeng/api";
import { AclService } from "src/app/common/services/acl.service";
import { LayoutService } from "src/app/layout/service/app.layout.service";

interface MenuCategory {
  items: MenuItem[];
  label: string;
  visible: boolean;
}

@Component({
  selector: "app-menu",
  templateUrl: "./app.menu.component.html",
  styleUrls: ["./app.menu.component.scss"],
})
export class AppMenuComponent implements OnInit {
  model: MenuCategory[] = [];

  private readonly aclService = inject(AclService);

  constructor(public layoutService: LayoutService) {}

  async ngOnInit(): Promise<void> {
    const canSeeRSICategory = await this.aclService.isAuthorized("SEE_RSI_CATEGORY");
    const canSeeRTRCategory = await this.aclService.isAuthorized("SEE_RTR_CATEGORY");
    const canSeeRTSCategory = await this.aclService.isAuthorized("SEE_RTS_CATEGORY");
    const canSeeSRFCategory = await this.aclService.isAuthorized("SEE_SRF_CATEGORY");
    const canSeeSWICategory = await this.aclService.isAuthorized("SEE_SWI_CATEGORY");
    const canSeeGeneralCategory = await this.aclService.isAuthorized("SEE_GENERAL_CATEGORY");
    const canSeeWatchSummaryCategory = await this.aclService.isAuthorized("SEE_WATCH_SUMMARY_CATEGORY");

    const canSeeEntitiesPage = await this.aclService.isAuthorized("SEE_ENTITIES_PAGE");
    const canSeeDashboard = await this.aclService.isAuthorized("SEE_STATS");

    const canSeeCrawlCareerDevelopmentAndTraining = await this.aclService.isAuthorized(
      "SEE_CRAWL_CAREER_DEVELOPMENT_TRAINING",
    );
    const canSeeCrawlAutomaticSummaryRegionalPress = await this.aclService.isAuthorized(
      "SEE_CRAWL_SUMMARY_REGIONAL_PRESS",
    );

    this.model = [
      {
        label: "RTS",
        visible: canSeeRTSCategory,
        items: [
          {
            label: $localize`:@@app_menu_sidebar_rts_info:PLATEFORMES PROPRES`,
            items: [
              {
                label: $localize`:@@app_menu_sidebar_rts_info_articles:Articles RTSinfo`,
                icon: "fa fa-newspaper-o",
                routerLink: ["/articles"],
                queryParams: { businessUnit: "RTS" },
              },
              {
                label: "Play RTS",
                icon: "fa fa-television",
                routerLink: ["/play-rts"],
              },
              {
                label: "RTS.ch Audio & Podcast",
                icon: "fa fa-microphone",
                routerLink: ["/radio"],
              },
            ],
          },
          {
            label: $localize`:@@app_menu_sidebar_social_research:RÉSEAUX SOCIAUX`,
            items: [
              {
                label: $localize`:@@app_menu_sidebar_social_research_instagram:Instagram`,
                icon: "fa fa-instagram",
                routerLink: ["/instagram"],
              },
            ],
          },
          {
            label: $localize`:@@app_menu_sidebar_title_dashboard:TABLEAU DE BORD`,
            visible: canSeeEntitiesPage || canSeeDashboard,
            items: [
              {
                label: $localize`:@@app_menu_sidebar_entities:Entités`,
                icon: "fa fa-ticket",
                routerLink: ["/entities"],
                visible: canSeeEntitiesPage,
              },
              {
                label: $localize`:@@app_menu_sidebar_dashboard:Dashboard`,
                icon: "fa fa-signal",
                routerLink: ["/dashboard"],
                visible: canSeeDashboard,
              },
            ],
          },
        ],
      },
      {
        label: "SRF",
        visible: canSeeSRFCategory,
        items: [
          {
            label: $localize`:@@app_menu_sidebar_srf:PLATEFORMES PROPRES`,
            items: [
              {
                label: $localize`:@@app_menu_sidebar_srf_articles:Articles SRF`,
                icon: "fa fa-newspaper-o",
                routerLink: ["/articles"],
                queryParams: { businessUnit: "SRF" },
              },
            ],
          },
        ],
      },
      {
        label: "RSI",
        visible: canSeeRSICategory,
        items: [
          {
            label: $localize`:@@app_menu_sidebar_rsi:PLATEFORMES PROPRES`,
            items: [
              {
                label: $localize`:@@app_menu_sidebar_rsi_articles:Articles RSI`,
                icon: "fa fa-newspaper-o",
                routerLink: ["/articles"],
                queryParams: { businessUnit: "RSI" },
              },
            ],
          },
        ],
      },
      {
        label: "RTR",
        visible: canSeeRTRCategory,
        items: [
          {
            label: $localize`:@@app_menu_sidebar_rtr:PLATEFORMES PROPRES`,
            items: [
              {
                label: $localize`:@@app_menu_sidebar_rtr_articles:Articles RTR`,
                icon: "fa fa-newspaper-o",
                routerLink: ["/articles"],
                queryParams: { businessUnit: "RTR" },
              },
            ],
          },
        ],
      },
      {
        label: "SWI",
        visible: canSeeSWICategory,
        items: [
          {
            label: $localize`:@@app_menu_sidebar_swi:PLATEFORMES PROPRES`,
            items: [
              {
                label: $localize`:@@app_menu_sidebar_swi_articles:Articles SWI`,
                icon: "fa fa-newspaper-o",
                routerLink: ["/articles"],
                queryParams: { businessUnit: "SWI" },
              },
            ],
          },
        ],
      },
      {
        label: $localize`:@@app_menu_category_general:GÉNÉRAL`,
        visible: canSeeGeneralCategory,
        items: [
          {
            label: $localize`:@@app_menu_sidebar_open_media:Open Media`,
            items: [
              {
                label: $localize`:@@app_menu_sidebar_agency_news:Agences de presse`,
                icon: "fa fa-file-text-o",
                routerLink: ["/news"],
              },
            ],
          },
          {
            label: $localize`:@@app_menu_sidebar_system_production:Systèmes de production`,
            items: [
              {
                label: $localize`:@@app_menu_sidebar_audiovisual_media:Médias audiovisuels & Sony Hive`,
                icon: "fa fa-file-audio-o",
                routerLink: ["/audiovisual-media"],
              },
            ],
          },
          {
            label: $localize`:@@app_menu_sidebar_transcriptions_and_translations:TRANSCRIPTIONS & TRADUCTIONS`,
            items: [
              {
                label: $localize`:@@app_menu_sidebar_audio_and_videos:Audios & Vidéos`,
                icon: "fa fa-volume-up",
                routerLink: ["/transcriptions"],
              },
              {
                label: $localize`:@@app_menu_sidebar_subtitles:Sous-titres`,
                icon: "fa fa-cc",
                routerLink: ["/translation"],
              },
            ],
          },
          {
            label: $localize`:@@app_menu_sidebar_prompts:Prompts`,
            items: [
              {
                label: $localize`:@@app_menu_sidebar_chat:Chat`,
                icon: "fa fa-comments-o",
                routerLink: ["/prompts"],
              },
              {
                label: $localize`:@@app_menu_sidebar_documents:Documents`,
                badge: "Test",
                icon: "pi pi-file-pdf",
                routerLink: ["/documents"],
              },
              {
                label: $localize`:@@documents_space_list_title:Espaces de documents`,
                badge: "Experimental",
                icon: "pi pi-file-pdf",
                routerLink: ["/documents-spaces"],
              },
            ],
          },
          {
            label: $localize`:@@app_menu_sidebar_research_summary:Résumés de Veille`,
            badge: "Test",
            visible: canSeeWatchSummaryCategory,
            items: [
              {
                label: $localize`:@@app_menu_sidebar_format_development:Développement des métiers et formations`,
                icon: "fa fa-search",
                routerLink: ["/website-crawling-assistant", "rts-formation"],
                visible: canSeeCrawlCareerDevelopmentAndTraining,
              },
              {
                label: $localize`:@@app_menu_sidebar_regional_press_summary:Résumé automatique de la Presse Régionale`,
                icon: "fa fa-search",
                routerLink: ["/website-crawling-assistant", "RTS-actu-rubrique-info-suisse"],
                visible: canSeeCrawlAutomaticSummaryRegionalPress,
              },
            ],
          },
        ],
      },
    ];
  }
}
